import { useState } from "react";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useAuth } from "hooks/useAuth";
import GroupMemberAddModal from "applications/signal/components/GroupMemberAddModal";
import ListPaginator from 'applications/signal/components/ListPaginator';
import { groupMemberTypeLabel } from "applications/signal/utils/viewUtils";
import { useDeleteSignalGroupMember } from "applications/signal/hooks/gqlHooks";
import { AlertSnackbarProps } from "features/components/alertSnackbar";
import { SignalUser } from "gql/graphql";
import { setGlobalMessage } from "hooks/useGlobalMessage";

type Props = {
  rows: SignalUser[];
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  totalCount: number;
  setSnackBarMessage: (message: AlertSnackbarProps | null) => void;
};

export default function GroupMemberList({
  rows, page, rowsPerPage, setPage, setRowsPerPage, totalCount, setSnackBarMessage
}: Props) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState<boolean>(false);
  const { signalUser } = useAuth();
  const { deleteSignalGroupMember } = useDeleteSignalGroupMember();

  const handleNewMemberClick = () => {
    setIsAddModalOpen(true);
  };

  const handleDeleteMemberClick = async (signalGroupMemberId: string) => {
    if (window.confirm('本当に削除しますか？')) {
      if (!signalUser || !signalGroupMemberId) return;
      setDeleteProcessing(true);
      const { data: result, error } = await deleteSignalGroupMember({data: {
        signal_group_id: signalUser.signal_group_id,
        signal_group_member_id: signalGroupMemberId
      }});
      if (error) {
        setGlobalMessage({
          message: "グループメンバーの除外に失敗しました。",
          severity: "error",
        });
      } else {
        setSnackBarMessage({
          message: "グループメンバーを除外しました。",
          severity: "success",
        });
      }
      setDeleteProcessing(false);
    }
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <h2 style={{ marginLeft: "20px" }}>メンバー管理</h2>
      <Box sx={{ display: 'flex', justifyContent: 'end', lineHeight: '21px', padding: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={handleNewMemberClick}
        >
          追加
        </Button>
      </Box>
      <TableContainer>
        <Table stickyHeader area-label="sticky table">
          <TableHead>
            <TableRow key="header">
              <TableCell align={"center"}>
                メールアドレス
              </TableCell>
              <TableCell align={"center"}>
                氏名
              </TableCell>
              <TableCell align={"center"}>
                メンバー種別
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={`${row.user_id}`}>
                    <TableCell key={`email-${index}`} align={"center"}>
                      {row.email}
                    </TableCell>
                    <TableCell key={`name-${index}`} align={"center"}>
                      {`${row.last_name} ${row.first_name}`}
                    </TableCell>
                    <TableCell key={`member-type-${index}`} align={"center"}>
                      {groupMemberTypeLabel(row.member_type)}
                    </TableCell>
                    <TableCell key={`actions-${index}`} align={"right"}>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<PersonRemoveIcon />}
                        onClick={() => handleDeleteMemberClick(row.signal_group_member_id!)}
                        disabled={deleteProcessing}
                      >
                        グループから除外
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="flex-end">
        <ListPaginator
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Box>
      <GroupMemberAddModal open={isAddModalOpen} setOpen={setIsAddModalOpen} setSnackBarMessage={setSnackBarMessage} />
    </Paper>
  )
}
